import { useMemo, useState } from "react";
import { ExternalLink, Newspaper } from "lucide-react";
import { PRESS_MENTIONS } from "../data/pressMentions";

const PAGE_SIZE = 6;

/** "In the News" feed. Filter controls only render once there's something real to filter — an empty dropdown is worse than no dropdown. */
export default function PressMentionsFeed() {
  const [outletFilter, setOutletFilter] = useState("All");
  const [yearFilter, setYearFilter] = useState("All");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const outlets = useMemo(
    () => Array.from(new Set(PRESS_MENTIONS.map((m) => m.outlet))).sort(),
    []
  );
  const years = useMemo(
    () => Array.from(new Set(PRESS_MENTIONS.map((m) => m.publishedAt.slice(0, 4)))).sort().reverse(),
    []
  );

  const filtered = useMemo(() => {
    return PRESS_MENTIONS.filter((m) => {
      if (outletFilter !== "All" && m.outlet !== outletFilter) return false;
      if (yearFilter !== "All" && !m.publishedAt.startsWith(yearFilter)) return false;
      return true;
    }).sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));
  }, [outletFilter, yearFilter]);

  if (PRESS_MENTIONS.length === 0) {
    return (
      <div className="rounded-2xl bg-foam-50 p-8 text-center dark:bg-white/5">
        <Newspaper className="mx-auto h-5 w-5 text-ocean-800/30 dark:text-foam-100/30" strokeWidth={1.5} />
        <p className="mt-2 text-sm text-ocean-800/60 dark:text-foam-100/60">
          No press coverage to share yet — check back as ZenFresh grows.
        </p>
      </div>
    );
  }

  const visible = filtered.slice(0, visibleCount);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {outlets.length > 1 && (
          <select
            value={outletFilter}
            onChange={(e) => setOutletFilter(e.target.value)}
            aria-label="Filter by outlet"
            className="rounded-full border border-ocean-900/10 bg-foam-50 px-3 py-1.5 text-xs font-medium text-ocean-800 dark:border-white/10 dark:bg-white/5 dark:text-foam-100"
          >
            <option value="All">All outlets</option>
            {outlets.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        )}
        {years.length > 1 && (
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            aria-label="Filter by year"
            className="rounded-full border border-ocean-900/10 bg-foam-50 px-3 py-1.5 text-xs font-medium text-ocean-800 dark:border-white/10 dark:bg-white/5 dark:text-foam-100"
          >
            <option value="All">All years</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="mt-6 text-sm text-ocean-800/60 dark:text-foam-100/60">No mentions match these filters.</p>
      ) : (
        <ul className="mt-6 space-y-3">
          {visible.map((mention) => (
            <li key={mention.articleUrl}>
              <a
                href={mention.articleUrl}
                target="_blank"
                rel="noreferrer"
                className="flex items-start justify-between gap-3 rounded-xl bg-foam-50 p-4 transition-colors hover:bg-foam-100 dark:bg-white/5 dark:hover:bg-white/10"
              >
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-wide text-teal-600">{mention.outlet}</p>
                  <p className="mt-1 font-medium text-ocean-950 dark:text-foam-50">{mention.headline}</p>
                  <p className="mt-1 text-xs text-ocean-800/50 dark:text-foam-100/50">{mention.publishedAt}</p>
                </div>
                <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-ocean-800/40 dark:text-foam-100/40" />
              </a>
            </li>
          ))}
        </ul>
      )}

      {visibleCount < filtered.length && (
        <button
          type="button"
          onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
          className="mt-6 w-full rounded-full py-2.5 text-sm font-semibold text-teal-600 ring-1 ring-teal-400/30 hover:bg-teal-400/10"
        >
          Show more
        </button>
      )}
    </div>
  );
}
